import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { invalidatePermissionQueries } from '@/hooks/usePermission'
import { confirmWorkspaceAction } from '@/services/api/workspace-settings.service'
import { parseApiError } from '@/utils/error-parser'

/**
 * Landing page of the e-mailed owner links (accept ownership, confirm deletion). The token sits in
 * the URL fragment so it never reaches a server log; it is read once, removed from the address bar
 * and only sent when the person presses the button.
 */
export function ConfirmPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get('token') ?? '')
  const confirm = useMutation({
    mutationFn: () => confirmWorkspaceAction(token),
    // Ownership changed: the cached "am I the owner" answer is wrong for up to 30 s otherwise.
    onSuccess: () => invalidatePermissionQueries(queryClient),
  })

  useEffect(() => {
    if (window.location.hash) window.history.replaceState(null, '', window.location.pathname)
  }, [])

  if (confirm.isSuccess) {
    const deleted = confirm.data.action === 'DELETE_WORKSPACE'
    return (
      <Shell title={deleted ? 'Workspace deleted' : 'You are now the owner'}
        text={deleted ? `${confirm.data.workspaceName} was deleted.` : `You now own ${confirm.data.workspaceName}.`}>
        <Button onClick={() => navigate(deleted ? '/workspace-select' : '/', { replace: true })}>Continue</Button>
      </Shell>
    )
  }
  if (!token) {
    return <Shell title="Link not valid" text="This link is incomplete. Open the link from your email again."><Button asChild><Link to="/">Go home</Link></Button></Shell>
  }
  return (
    <Shell title="Confirm" text="Press confirm to finish. You must be signed in with the account the email was sent to.">
      {confirm.isError && <p className="text-sm text-destructive">{parseApiError(confirm.error).detail || 'Could not complete this action.'}</p>}
      <Button disabled={confirm.isPending} onClick={() => confirm.mutate()}>{confirm.isPending ? 'Working…' : 'Confirm'}</Button>
    </Shell>
  )
}

function Shell({ title, text, children }: { title: string; text: string; children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader><CardTitle>{title}</CardTitle><CardDescription>{text}</CardDescription></CardHeader>
        <CardContent className="space-y-3">{children}</CardContent>
      </Card>
    </div>
  )
}
